// interlinked-tdd: exempt -- presentational; rendering behavior is exercised
// through MessageList.test.tsx (which renders these via <MessageList>) and,
// for ToolCallCard's header directly, MessageParts.test.tsx. Going through
// <MessageList> alone left the duration badge below with zero coverage —
// deleting it kept the whole repo green — so header affordances get a direct
// render test, not an inherited one.
//
// Tool-call + reasoning render components, split out of MessageList and wrapped
// in React.memo so a streaming turn (word-level chunks, dozens of sandbox
// cards) only re-renders the parts that actually changed — not every prior
// card. CodeBlock in particular is expensive (syntax highlighting), so memo
// keeps it from re-tokenizing on unrelated re-renders.
import { memo, useState } from "react";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import jsLang from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
// Relative (not "@/lib/…") so this resolves under the root vitest alias when
// MessageList.test.tsx renders these. See feedback_cf_src_vitest_alias.
import { cn } from "../lib/utils";
import {
  describeCodemodeCall,
  summarizeToolDuration,
  summarizeToolOutput,
  type SummaryTone,
} from "../lib/tool-summary";

SyntaxHighlighter.registerLanguage("javascript", jsLang);

export interface ToolPart {
  type: string;
  toolCallId?: string;
  toolName?: string;
  state?: "input-streaming" | "input-available" | "output-available" | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

// Claude-Code-style thinking: dim, italic, never empty. Open by default only
// while it's the live streaming thought; collapses once the turn moves on. A
// click pins the user's choice for that block.
export const ReasoningBlock = memo(function ReasoningBlock({
  text,
  active,
}: {
  text: string;
  active: boolean;
}) {
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? active;
  return (
    <div data-testid="reasoning-block" className="my-1">
      <button
        type="button"
        onClick={() => setOverride(!open)}
        className="flex items-center gap-1.5 text-[11px] italic text-muted-foreground/70 transition-colors hover:text-muted-foreground"
      >
        <span className={cn("not-italic", active && "animate-pulse")}>✻</span>
        <span>{active ? "Thinking…" : "Thought"}</span>
        <span className="not-italic opacity-60">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="mt-1 whitespace-pre-wrap border-l-2 border-border/60 pl-3 text-xs italic leading-relaxed text-muted-foreground/85">
          {text}
        </div>
      )}
    </div>
  );
});

// A dark, terminal-style panel regardless of app theme — code reads as code,
// the way a coding agent shows the command it ran. Memoized: syntax
// highlighting is expensive, and the code string never changes once set.
const CodeBlock = memo(function CodeBlock({ code }: { code: string }) {
  return (
    <SyntaxHighlighter
      language="javascript"
      style={oneDark}
      customStyle={{
        margin: 0,
        borderRadius: "0.375rem",
        fontSize: "11px",
        lineHeight: 1.5,
        padding: "0.5rem 0.625rem",
        background: "rgb(24 24 27)",
      }}
      codeTagProps={{
        style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
      }}
      wrapLongLines
    >
      {code}
    </SyntaxHighlighter>
  );
});

const TONE_CLASS: Record<SummaryTone, string> = {
  ok: "text-muted-foreground",
  warn: "text-amber-600 dark:text-amber-400",
  error: "text-destructive",
};

/** Pull the `code` string off a codemode_sandbox tool input, if present. */
function extractCode(input: unknown): string | undefined {
  const obj = input as { code?: unknown } | null | undefined;
  return obj && typeof obj === "object" && typeof obj.code === "string"
    ? obj.code
    : undefined;
}

function renderOutput(output: unknown): string {
  return typeof output === "string" ? output : JSON.stringify(output, null, 2);
}

// The expandable detail of a tool call. For codemode_sandbox the star is the JS
// program (shown as highlighted code); for direct tools it's the input JSON.
const ToolCardBody = memo(function ToolCardBody({
  part,
  isCodemode,
  codeStr,
}: {
  part: ToolPart;
  isCodemode: boolean;
  codeStr: string | undefined;
}) {
  return (
    <div className="space-y-2 border-t border-border/50 px-3 py-2 font-mono text-[11px]">
      {isCodemode && codeStr !== undefined ? (
        <div>
          <div className="mb-0.5 text-muted-foreground">code</div>
          <CodeBlock code={codeStr} />
        </div>
      ) : (
        part.input !== undefined && (
          <div>
            <div className="mb-0.5 text-muted-foreground">input</div>
            <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted/40 p-2">
              {JSON.stringify(part.input, null, 2)}
            </pre>
          </div>
        )
      )}
      {part.state === "output-error" && part.errorText && (
        <div>
          <div className="mb-0.5 text-destructive">error</div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-destructive/10 p-2 text-destructive">
            {part.errorText}
          </pre>
        </div>
      )}
      {part.output !== undefined && (
        <div>
          <div className="mb-0.5 text-muted-foreground">
            {isCodemode ? "result" : "output"}
          </div>
          <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded bg-muted/40 p-2">
            {renderOutput(part.output)}
          </pre>
        </div>
      )}
    </div>
  );
});

export const ToolCallCard = memo(function ToolCallCard({ part }: { part: ToolPart }) {
  const rawName = part.toolName || part.type.replace(/^tool-/, "");
  const isCodemode = rawName === "codemode_sandbox";
  const codeStr = isCodemode ? extractCode(part.input) : undefined;
  const desc = isCodemode ? describeCodemodeCall(codeStr) : undefined;
  // codemode_sandbox routes every server call through one tool, so name the
  // card by the helper call its program makes (e.g. "Sandbox · civic_execute")
  // rather than the opaque tool name.
  const displayName = desc
    ? `Sandbox · ${desc.label}`
    : rawName.replace(/__/g, " · ").replace(/_/g, " ");

  const state = part.state ?? "input-streaming";
  const isRunning = state === "input-streaming" || state === "input-available";
  const isError = state === "output-error";
  const isDone = state === "output-available";

  // One-line result hint on the header, like a terminal agent's "→ 8 results".
  // Discovery calls (listTools/describeTool) have no result count worth showing.
  const summary =
    isDone && desc?.kind !== "discover"
      ? summarizeToolOutput(part.output)
      : undefined;

  // Per-call wall clock, stamped server-side by stampTiming(). Shown for every
  // finished call including discovery ones — "which source was slow" is exactly
  // the question this answers, and unlike a result count it can't mislead.
  const duration = isDone ? summarizeToolDuration(part.output) : null;

  // Collapsed by default; expand on error so the user sees what the model saw.
  const [collapsed, setCollapsed] = useState(!isError);

  return (
    <div
      data-testid="tool-call-card"
      data-tool-name={rawName}
      data-tool-state={state}
      className={cn(
        "my-1 rounded-lg border bg-background/60 text-xs",
        isError && "border-destructive/50 bg-destructive/5",
        isDone && "border-emerald-500/30",
        isRunning && "border-blue-500/40",
      )}
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-accent/50"
      >
        <ToolStatusIcon state={state} />
        <span className="shrink-0 font-medium">{displayName}</span>
        {summary?.text && (
          <span
            className={cn(
              "truncate font-mono text-[11px]",
              TONE_CLASS[summary.tone],
            )}
          >
            → {summary.text}
          </span>
        )}
        {isRunning && (
          <span className="font-mono text-[11px] text-muted-foreground">
            running…
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {duration && (
            <span
              data-testid="tool-call-duration"
              className="font-mono text-[11px] tabular-nums text-muted-foreground/70"
            >
              {duration}
            </span>
          )}
          <span className="text-muted-foreground">{collapsed ? "▸" : "▾"}</span>
        </span>
      </button>
      {!collapsed && (
        <ToolCardBody part={part} isCodemode={isCodemode} codeStr={codeStr} />
      )}
    </div>
  );
});

function ToolStatusIcon({ state }: { state: ToolPart["state"] }) {
  if (state === "output-error") {
    return (
      <span
        className="flex h-4 w-4 items-center justify-center rounded-full bg-destructive/15 text-destructive"
        aria-label="error"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className="h-2.5 w-2.5"
        >
          <path d="M2 2 L14 14 M14 2 L2 14" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (state === "output-available") {
    return (
      <span
        className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"
        aria-label="success"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.4}
          className="h-2.5 w-2.5"
        >
          <path d="M3 8 L7 12 L13 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  // running
  return (
    <span
      className="flex h-4 w-4 items-center justify-center text-blue-500"
      aria-label="running"
    >
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5 animate-spin">
        <circle
          cx={8}
          cy={8}
          r={6}
          stroke="currentColor"
          strokeOpacity={0.25}
          strokeWidth={2}
        />
        <path
          d="M14 8a6 6 0 0 0-6-6"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
